import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import { setCarouselPageIsCompletedAction } from 'src/components/shared/SciPage/SciPage.duck';
import { SciPagesEnum } from 'src/components/shared/SciPage/types';

import {
    productsAreLoadingSelector,
    vehicleProtectionInsurancesSelector,
    vehicleProtectionUpgradesSelector,
} from '../shared/products.selector';
import { productGetAction, productUpdateAction } from '../shared/products.duck';

import { VehicleProtectionsFormValues } from './types';
import msg from './messages';
import { VEHICLE_PROTECTION_TYPES } from './vehicleProtections.constants';

export const useVehicleProtectionsForm = () => {
    const dispatch = useDispatch();
    const isLoading = useSelector(productsAreLoadingSelector);
    const insurances = useSelector(vehicleProtectionInsurancesSelector);
    const upgrade = useSelector(vehicleProtectionUpgradesSelector);
    const intl = useIntl();

    const schema = yup.object().shape({
        insurance: yup
            .string()
            .required(intl.formatMessage(msg.formHeaderItems.insuranceError)),
        upgrade: yup.boolean(),
    });

    const {
        control,
        reset,
        watch,
        handleSubmit,
        formState: { errors: formErrors },
    } = useForm<VehicleProtectionsFormValues>({
        resolver: yupResolver(schema),
    });

    useEffect(() => {
        const subscription = watch((data, info) => {
            const isManualChange = !!info?.name;

            if (isManualChange) {
                if (info.name === VEHICLE_PROTECTION_TYPES.insurance) {
                    const insurance = insurances.find(
                        (i) => i.id.productId === data.insurance,
                    );

                    if (insurance) {
                        dispatch(
                            productUpdateAction({
                                ...insurance,
                                quantity: 1,
                            }),
                        );
                    }
                } else if (info.name === VEHICLE_PROTECTION_TYPES.upgrade) {
                    dispatch(
                        productUpdateAction({
                            ...upgrade,
                            quantity: upgrade.quantity === 0 ? 1 : 0,
                        }),
                    );
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, dispatch, insurances, upgrade]);

    useEffect(() => {
        // Reset value once getting response from BE
        const resettedValue = {
            insurance: insurances.reduce((acc, currentValue) => {
                return currentValue.quantity > 0
                    ? currentValue.id.productId
                    : acc;
            }, ''),
            upgrade: upgrade?.quantity > 0,
        };

        reset(resettedValue);
    }, [insurances, upgrade, reset]);

    useEffect(() => {
        dispatch(productGetAction());
    }, [dispatch]);

    const onSubmit = () => {
        dispatch(
            setCarouselPageIsCompletedAction({
                page: SciPagesEnum.VehicleProtections,
            }),
        );
    };

    const upgradeSelected = watch('upgrade');

    return {
        control,
        isLoading,
        insurances,
        upgrade,
        upgradeSelected,
        formErrors,
        onSubmit: handleSubmit(onSubmit),
    };
};
